import { useState, useEffect } from "react";
import { useAuth } from "@/context/AuthContext";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import api from "@/lib/api";
import { Search, Filter, Calendar, Eye, AlertCircle, Loader2, Download } from "lucide-react";
import { parseISO, isAfter, endOfDay } from "date-fns";
import { toast } from "sonner";

const StatusBadge = ({ status }) => {
  const statusConfig = {
    pendiente: { label: "Pendiente", className: "bg-amber-100 text-amber-700 border-amber-200" },
    aprobado: { label: "Aprobada", className: "bg-emerald-100 text-emerald-700 border-emerald-200" },
    rechazado: { label: "Rechazada", className: "bg-red-100 text-red-700 border-red-200" },
    anulado: { label: "Anulada", className: "bg-slate-100 text-slate-700 border-slate-200" },
  };

  const config = statusConfig[status] || statusConfig.pendiente;

  return (
    <Badge variant="outline" className={config.className}>
      {config.label}
    </Badge>
  );
};

const HistorialPage = () => {
  const { user } = useAuth();
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("todos");
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [detailOpen, setDetailOpen] = useState(false);
  const [cancelOpen, setCancelOpen] = useState(false);
  const [cancelReason, setCancelReason] = useState("");
  const [processing, setProcessing] = useState(false);

  useEffect(() => {
    const fetchRequests = async () => {
      try {
        const response = await api.getVacationRequests();
        setRequests(response.data);
      } catch (error) {
        console.error("Error fetching requests:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchRequests();
  }, []);

  const formatDate = (dateString) => {
    if (!dateString) return "-";
    try {
      // Usar parseISO para evitar el desfase por zona horaria UTC
      return parseISO(dateString).toLocaleDateString("es-GT", {
        day: "numeric",
        month: "short",
        year: "numeric",
      });
    } catch (e) {
      return dateString;
    }
  };

  const filteredRequests = requests.filter((req) => {
    const matchesSearch =
      req.motivo?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      req.empleado_nombre?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = statusFilter === "todos" || req.estado === statusFilter;
    return matchesSearch && matchesStatus;
  });

  const handleViewDetail = (request) => {
    setSelectedRequest(request);
    setDetailOpen(true);
  };

  const handleOpenCancel = (request) => {
    setSelectedRequest(request);
    setCancelReason("");
    setCancelOpen(true);
  };

  const handleConfirmCancel = async () => {
    if (!cancelReason.trim()) {
      toast.error("Debe ingresar un motivo para anular la vacación");
      return;
    }
    setProcessing(true);
    try {
      await api.cancelRequest(selectedRequest.id, cancelReason);
      toast.success("Vacaciones anuladas correctamente");
      setCancelOpen(false);
      const response = await api.getVacationRequests();
      setRequests(response.data);
    } catch (error) {
      console.error("Error al anular:", error);
      toast.error(error.response?.data?.detail || "Error al anular las vacaciones");
    } finally {
      setProcessing(false);
    }
  };

  const handleDownloadPdf = async (req) => {
    try {
      toast.info("Generando boleta PDF...");
      const response = await api.downloadVacationSlip(req.id);
      
      const blob = new Blob([response.data], { type: "application/pdf" });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      
      const corrStr = req.correlativo ? String(req.correlativo).padStart(5, '0') : '00000';
      link.setAttribute("download", `Boleta_Vacaciones_No_${corrStr}.pdf`);
      
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      toast.success("Boleta descargada con éxito");
    } catch (error) {
      console.error("Error al descargar boleta:", error);
      toast.error("Error al descargar la boleta PDF");
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-10 h-10 border-4 border-orange-500 border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fadeIn" data-testid="historial-page">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-slate-800 tracking-tight">
            Historial de Solicitudes
          </h1>
          <p className="text-slate-500 mt-1">
            Revisa todas tus solicitudes de vacaciones
          </p>
        </div>
      </div>

      {/* Filters */}
      <Card className="border-slate-100">
        <CardContent className="p-4">
          <div className="flex flex-col md:flex-row gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input
                placeholder="Buscar por motivo o nombre..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
                data-testid="search-input"
              />
            </div>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-full md:w-48" data-testid="status-filter">
                <Filter className="w-4 h-4 mr-2" />
                <SelectValue placeholder="Filtrar por estado" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="todos">Todos los estados</SelectItem>
                <SelectItem value="pendiente">Pendiente</SelectItem>
                <SelectItem value="aprobado">Aprobado</SelectItem>
                <SelectItem value="rechazado">Rechazado</SelectItem>
                <SelectItem value="anulado">Anulado</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Table */}
      <Card className="border-slate-100">
        <CardContent className="p-0">
          {filteredRequests.length > 0 ? (
            <Table>
              <TableHeader>
                <TableRow className="hover:bg-transparent">
                  <TableHead>Colaborador</TableHead>
                  <TableHead>Fecha Solicitud</TableHead>
                  <TableHead>Período</TableHead>
                  <TableHead>Días</TableHead>
                  <TableHead>Motivo</TableHead>
                  <TableHead>Estado</TableHead>
                  <TableHead className="text-right">Acciones</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredRequests.map((request) => (
                  <TableRow key={request.id} className="hover:bg-slate-50">
                    <TableCell>
                      <div className="flex flex-col">
                        <span className="font-medium text-slate-800">{request.empleado_nombre}</span>
                      </div>
                    </TableCell>
                    <TableCell className="text-slate-600">
                      {formatDate(request.fecha_creacion)}
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <Calendar className="w-4 h-4 text-sky-500" />
                        <span className="text-slate-800">
                          {formatDate(request.fecha_inicio)} - {formatDate(request.fecha_fin)}
                        </span>
                      </div>
                    </TableCell>
                    <TableCell>
                      <span className="font-medium text-slate-800">
                        {request.dias_solicitados}
                      </span>
                    </TableCell>
                    <TableCell>
                      <span className="text-slate-600 truncate max-w-[200px] block">
                        {request.motivo}
                      </span>
                    </TableCell>
                    <TableCell>
                      <StatusBadge status={request.estado} />
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-2">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleViewDetail(request)}
                          data-testid={`view-detail-${request.id}`}
                        >
                          <Eye className="w-4 h-4" />
                        </Button>
                        {request.estado === "aprobado" && (
                          <Button
                            variant="ghost"
                            size="sm"
                            className="text-sky-600 hover:text-sky-700 hover:bg-sky-50"
                            onClick={() => handleDownloadPdf(request)}
                            title="Descargar Boleta PDF"
                          >
                            <Download className="w-4 h-4" />
                          </Button>
                        )}
                        {(request.estado === "aprobado" || request.estado === "pendiente") && 
                         (user?.rol === "administrador" || (user?.rol === "supervisor" && user?.id === request.supervisor_id)) && 
                         (!request.fecha_fin || !isAfter(new Date(), endOfDay(parseISO(request.fecha_fin)))) && (
                          <Button
                            variant="outline"
                            size="sm"
                            className="text-red-600 border-red-200 hover:bg-red-50"
                            onClick={() => handleOpenCancel(request)}
                            title="Anular Vacaciones"
                          >
                            Anular
                          </Button>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          ) : (
            <div className="text-center py-12">
              <Calendar className="w-12 h-12 text-slate-300 mx-auto mb-3" />
              <p className="text-slate-500">No se encontraron solicitudes</p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Detail Dialog */}
      <Dialog open={detailOpen} onOpenChange={setDetailOpen}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>Detalle de Solicitud</DialogTitle>
            <DialogDescription>Información completa de la solicitud de vacaciones</DialogDescription>
          </DialogHeader>
          {selectedRequest && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-slate-500">Colaborador</p>
                  <p className="font-medium text-slate-800">{selectedRequest.empleado_nombre}</p>
                </div>
                <div>
                  <p className="text-sm text-slate-500">Email</p>
                  <p className="font-medium text-slate-800">{selectedRequest.empleado_email}</p>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-slate-500">Estado</p>
                  <StatusBadge status={selectedRequest.estado} />
                </div>
                <div>
                  <p className="text-sm text-slate-500">Días Solicitados</p>
                  <p className="font-semibold text-slate-800">{selectedRequest.dias_solicitados}</p>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-slate-500">Fecha Inicio</p>
                  <p className="font-medium text-slate-800">{formatDate(selectedRequest.fecha_inicio)}</p>
                </div>
                <div>
                  <p className="text-sm text-slate-500">Fecha Fin</p>
                  <p className="font-medium text-slate-800">{formatDate(selectedRequest.fecha_fin)}</p>
                </div>
              </div>
              <div>
                <p className="text-sm text-slate-500">Motivo</p>
                <p className="text-slate-800">{selectedRequest.motivo}</p>
              </div>
              <div>
                <p className="text-sm text-slate-500">Supervisor</p>
                <p className="text-slate-800">{selectedRequest.supervisor_nombre || selectedRequest.supervisor_email}</p>
              </div>
              {selectedRequest.comentario_supervisor && (
                <div className="p-4 bg-slate-50 rounded-lg">
                  <p className="text-sm text-slate-500 mb-1">Comentario del Supervisor</p>
                  <p className="text-slate-800">{selectedRequest.comentario_supervisor}</p>
                </div>
              )}
              <div className="text-xs text-slate-400">
                Creada: {formatDate(selectedRequest.fecha_creacion)}
                {selectedRequest.fecha_respuesta && (
                  <> • Respondida: {formatDate(selectedRequest.fecha_respuesta)}</>
                )}
              </div>
              {selectedRequest.estado === "aprobado" && (
                <div className="flex justify-end pt-4 border-t mt-4">
                  <Button
                    onClick={() => handleDownloadPdf(selectedRequest)}
                    className="bg-sky-600 hover:bg-sky-700 text-white"
                  >
                    <Download className="w-4 h-4 mr-2" />
                    Descargar Boleta PDF
                  </Button>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
      <Dialog open={cancelOpen} onOpenChange={setCancelOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertCircle className="w-5 h-5 text-red-500" />
              Anular Vacaciones Aprobadas
            </DialogTitle>
            <DialogDescription>
              ¿Estás seguro que deseas anular estas vacaciones? Esta acción devolverá los días al colaborador.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <label className="text-sm font-medium text-slate-700">Motivo de la anulación (Obligatorio)</label>
              <Input
                placeholder="Ej. Error en fechas, necesidad operativa..."
                value={cancelReason}
                onChange={(e) => setCancelReason(e.target.value)}
              />
            </div>
          </div>

          <div className="flex justify-end gap-2">
            <Button
              variant="outline"
              onClick={() => setCancelOpen(false)}
              disabled={processing}
            >
              Cancelar
            </Button>
            <Button
              className="bg-red-500 hover:bg-red-600 text-white"
              onClick={handleConfirmCancel}
              disabled={processing || !cancelReason.trim()}
            >
              {processing ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Anulando...
                </>
              ) : (
                "Confirmar Anulación"
              )}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default HistorialPage;
